<template>
  <div class="users-page">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">User Management</h1>
        <p class="text-muted mb-0">View and manage registered users</p>
      </div>
      <button
        @click="refreshUsers"
        class="btn btn-outline-primary"
        :disabled="isLoading"
      >
        <i class="fas fa-sync-alt me-2" :class="{ 'fa-spin': isLoading }"></i>
        Refresh
      </button>
    </div>

    <!-- User Statistics Cards -->
    <div class="row mb-4" v-if="userStats">
      <div class="col-md-3">
        <div class="card bg-primary text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="card-title">{{ userStats.totalUsers }}</h4>
                <p class="card-text">Total Users</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-users fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-success text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="card-title">{{ userStats.activeUsers }}</h4>
                <p class="card-text">Active Users</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-user-check fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-info text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="card-title">{{ userStats.newUsersToday }}</h4>
                <p class="card-text">New Today</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-user-plus fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-warning text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="card-title">{{ userStats.newUsersThisWeek }}</h4>
                <p class="card-text">New This Week</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-calendar-week fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading users...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
      <button @click="loadUsers" class="btn btn-sm btn-outline-danger ms-2">
        <i class="fas fa-redo me-1"></i>
        Retry
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="allUsers.length === 0" class="text-center py-5">
      <i class="fas fa-users fa-4x text-muted mb-4"></i>
      <h4>No users found</h4>
      <p class="text-muted mb-4">No users have registered yet</p>
    </div>

    <!-- Users Table -->
    <div v-else class="card">
      <div class="card-header">
        <h5 class="mb-0">Registered Users ({{ allUsers.length }})</h5>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>User</th>
                <th>Mobile Number</th>
                <th>Country Code</th>
                <th>Joined</th>
                <th>Last Login</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in allUsers" :key="user.id">
                <td>
                  <div class="d-flex align-items-center">
                    <div class="avatar-circle me-3">
                      <i class="fas fa-user"></i>
                    </div>
                    <div>
                      <h6 class="mb-0">{{ user.username }}</h6>
                      <small class="text-muted">ID: {{ user.id }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge bg-light text-dark">{{
                    user.mobileNumber
                  }}</span>
                </td>
                <td>
                  <span class="badge bg-secondary"
                    >+{{ user.countryCode }}</span
                  >
                </td>
                <td>
                  <small>{{ formatDate(user.createdAt) }}</small>
                </td>
                <td>
                  <small v-if="user.lastLoginAt" class="text-success">
                    {{ formatDate(user.lastLoginAt) }}
                  </small>
                  <small v-else class="text-muted">Never</small>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="user.isActive ? 'bg-success' : 'bg-secondary'"
                  >
                    {{ user.isActive ? "Active" : "Inactive" }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "Users",
  computed: {
    ...mapGetters("users", ["allUsers", "userStats", "isLoading", "error"]),
  },
  methods: {
    ...mapActions("users", ["fetchUsers", "fetchUserStats"]),

    formatDate(dateString) {
      return moment(dateString).format("MMM DD, YYYY HH:mm");
    },

    async loadUsers() {
      try {
        await Promise.all([this.fetchUsers(), this.fetchUserStats()]);
      } catch (error) {
        console.error("Error loading users:", error);
      }
    },

    async refreshUsers() {
      await this.loadUsers();
    },
  },
  async created() {
    await this.loadUsers();
  },
};
</script>

<style scoped>
.users-page {
  animation: fadeIn 0.5s ease-out;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table th {
  border-top: none;
  font-weight: 600;
  color: var(--dark-color);
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
}

.loading {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.9rem;
  }

  .avatar-circle {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
}
</style>
